import type { AgentBillingAddShopifyThemeBuilderProjectsModalProps } from "@v2/types/modalTypes";

import * as React from "react";

import { SplitModal } from "@replohq/lattice/blocks/modal/SplitModal";
import { skipToken } from "@tanstack/react-query";
import { useCurrentWorkspace } from "@v2/contexts/WorkspaceDashboardContext";
import { analytics } from "@v2/features/infra/analytics/tracking";
import { useModal } from "@v2/store/modalStore";
import { workspaceRouter } from "@v2/trpc/routes/workspace";
import { formatNumber } from "replo-utils/lib/format";
import { pluralize } from "replo-utils/lib/pluralize";

import { showAgentBillingUpdateSuccessToast } from "../agentBillingToasts";
import AddSubscriptionItemContent from "../components/AddSubscriptionItemContent";
import useAgentApplySubscriptionChangeMutation from "../hooks/useAgentApplySubscriptionChangeMutation";
import { useAgentBillingSubscription } from "../hooks/useAgentSubscription";
import { useRateCards } from "../hooks/useRateCards";

const AgentBillingAddShopifyThemeBuilderProjectsModal: React.FC<
  AgentBillingAddShopifyThemeBuilderProjectsModalProps
> = () => {
  const workspace = useCurrentWorkspace();
  const modal = useModal();
  const { data: activeSubscription, currentDraft } =
    useAgentBillingSubscription();
  const { rateCards } = useRateCards();
  const { data: shopifyThemeProjectsUsage } =
    workspaceRouter.getShopifyThemeProjectsUsage.useQuery(
      workspace?.id ?? skipToken,
    );

  const { startSubscriptionChange, isLoading: isLoadingSubscriptionUpdate } =
    useAgentApplySubscriptionChangeMutation();

  if (
    !workspace ||
    !activeSubscription ||
    !rateCards ||
    !currentDraft ||
    !shopifyThemeProjectsUsage ||
    shopifyThemeProjectsUsage.maximum === null ||
    !activeSubscription.currentPeriodEnd ||
    // (yuxin, 2026-07-14, REPL-29329): custom-contract add-ons go through support.
    activeSubscription.hasCustomPricing
  ) {
    return null;
  }

  const rateCard = rateCards.find(
    (rateCard) => rateCard.plan === activeSubscription.plan,
  );
  const projectProduct = rateCard?.addOnProducts.find(
    (product) => product.featureType === "shopifyThemeBuilder.projects",
  );

  if (!projectProduct) {
    return null;
  }

  const closeModal = () => {
    modal.closeModal({
      type: "agentBillingAddShopifyThemeBuilderProjectsModal",
    });
  };

  return (
    <SplitModal
      open={true}
      onOpenChange={(open) => {
        if (!open) {
          closeModal();
        }
      }}
      media={
        <img
          src="/images/billing/shop-modal-graphic.png"
          alt=""
          className="h-full w-full object-cover"
        />
      }
    >
      <AddSubscriptionItemContent
        currentSubscriptionDraft={currentDraft}
        product={projectProduct}
        currentCount={shopifyThemeProjectsUsage.maximum}
        interval={activeSubscription.interval}
        currentPeriodEnd={activeSubscription.currentPeriodEnd}
        rateCards={rateCards}
        onConfirm={({ subscriptionView, additionalCount }) => {
          analytics.logEvent(
            "agentBilling.addShopifyThemeBuilderProjects.submitted",
            {
              currentPlan: activeSubscription.plan,
              additionalCount,
            },
          );

          startSubscriptionChange(
            {
              workspaceId: workspace.id,
              subscriptionView,
              successUrl: window.location.href,
              cancelUrl: window.location.href,
            },
            {
              onSuccess: (result) => {
                // A bank-approval change isn't applied yet; the approval notice replaces the success toast.
                if (result.confirmationKind !== "bankApproval") {
                  showAgentBillingUpdateSuccessToast(
                    `Added ${formatNumber(additionalCount)} additional ${pluralize(projectProduct.displayName, additionalCount)}`,
                    { showConfetti: true },
                  );
                }
                closeModal();
              },
            },
          );
        }}
        isLoading={isLoadingSubscriptionUpdate}
      />
    </SplitModal>
  );
};

export default AgentBillingAddShopifyThemeBuilderProjectsModal;
